import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  styled,
  TextField,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Image from '../../../../components/Image';
import MayBank from '../../../../assets/deposit/maybank.png';
import GameConsole from '../../../../assets/withdraw/joystick.png';
import Restore from '../../../../assets/withdraw/restore.png';
import RestoreIcon from '../../../../assets/deposit/restore.png';
import Login from '../../../../assets/login.png';
import Iconify from '../../../../components/Iconify';
import palette from '../../../../theme/palette';
import CustomDrawer from '../../../../components/CustomDrawer';
import Details from './Details'; // Adjust path as needed
import AddBankAccountDialog from './AddBankDetails';
import MemberBalanceSection from '../MemberBalanceSection';
import NonRootHeader from '../NonRootHeader';
import { suggestedAmounts } from '../../../../config';
import useLocales from '../../../../hooks/useLocales';
import AffinBank from '../../../../assets/banks/AffinBank.png';
import AllianceBank from '../../../../assets/banks/AllianceBank.png';
import AMBank from '../../../../assets/banks/AMBank.png';
import ArgoBank from '../../../../assets/banks/ArgoBank.png';
import BankIslamMalaysia from '../../../../assets/banks/BankIslamMalaysia.png';
import BankMuamalat from '../../../../assets/banks/BankMuamalat.png';
import BankRakyat from '../../../../assets/banks/BankRakyat.png';
import BSNBank from '../../../../assets/banks/BSNBank.png';
import CIMBBank from '../../../../assets/banks/CIMBBank.png';
import CitiBank from '../../../../assets/banks/CitiBank.png';
import GXBank from '../../../../assets/banks/GXBank.png';
import HongLeongBank from '../../../../assets/banks/HongLeongBank.png';
import HSBCBank from '../../../../assets/banks/HSBCBank.png';
import Maybank from '../../../../assets/banks/Maybank.png';
import MerchanttradeBank from '../../../../assets/banks/MerchantradeBank.png';
import OCBCBank from '../../../../assets/banks/OCBCBank.png';
import PublicBank from '../../../../assets/banks/PublicBank.png';
import RHBBank from '../../../../assets/banks/RHBBank.png';
import SecurePay from '../../../../assets/banks/SecurePay.png';
import StandardCharteredBank from '../../../../assets/banks/StandardCharteredBank.png';
import TouchGo from '../../../../assets/banks/TouchGo.png';
import UOBBank from '../../../../assets/banks/UOBBank.png';
import Card from '../../../../assets/profile/card.png';
import conObj, { conObjForm } from '../../../../utils/connection-assistant';
import LoadingScreen from '../../../../components/LoadingScreen';
// --------------------
// STYLES
// --------------------

const banksIcons = {
  maybank: Maybank,
  cimbbank: CIMBBank,
  publicbank: PublicBank,
  hongleongbank: HongLeongBank,
  rhbbank: RHBBank,
  ambank: AMBank,
  affinbank: AffinBank,
  bsnbank: BSNBank,
  alliancebank: AllianceBank,
  bankislammalaysia: BankIslamMalaysia,
  hsbcbank: HSBCBank,
  uobbank: UOBBank,
  citibank: CitiBank,
  ocbcbank: OCBCBank,
  standardcharteredbank: StandardCharteredBank,
  argobank: ArgoBank,
  bankmuamalat: BankMuamalat,
  bankrakyat: BankRakyat,
  gxbank: GXBank,
  merchanttradebank: MerchanttradeBank,
  securepay: SecurePay,
  touchandgo: TouchGo,
};

const getBankKey = (name) => {
  return name
    ?.toLowerCase()
    .replace(/&/g, 'and')
    .replace(/\s+/g, '')
    .replace(/[^a-z]/g, '');
};
const FormSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  padding: theme.spacing(3),
  borderRadius: '8px',
  // marginBottom: theme.spacing(2),

  // Mobile styles
  [theme.breakpoints.down('sm')]: {
    padding: 0,
    marginTop: '16px',
  },
}));

const Label = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 700,
  marginBottom: theme.spacing(1.5),
  color: '#111',
}));

const inputStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#F9FAFB',
    height: 52,
    '& fieldset': { border: '1px solid #E5E7EB' },
    '&:hover fieldset': { borderColor: '#D1D5DB' },
    '&.Mui-focused fieldset': { border: '1px solid #2563EB' },
  },
  '& input': { padding: '12px', fontWeight: 600 },
};
const BankCard = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})(({ theme, isSelected }) => ({
  width: '100%',
  justifyContent: 'flex-start',
  padding: '16px',
  borderRadius: '12px',
  border: `2px solid ${theme.palette.gradients.buttonPrimary}`,
  background: isSelected ? theme.palette.gradients.buttonPrimary : '#F3F4F6',
  // background: palette.header.register_btn.bg,
  // border: `2px solid ${palette.header.register_btn.border}`,
  color: '#000',
  textTransform: 'none',
  display: 'flex',
  gap: '12px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: isSelected ? theme.palette.gradients.buttonPrimary2 : '#E5E7EB',
  },
}));

const Withdraw = () => {
  const { translate } = useLocales();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { memberInfo } = useSelector((x) => ({
    ...x.providers,
    ...x.lookup,
    ...x.game,
  }));

  useEffect(() => {
    const bg = document.querySelector('img[alt="Background"]');

    if (bg) {
      bg.style.visibility = 'hidden';
    }

    return () => {
      if (bg) {
        bg.style.visibility = 'visible';
      }
    };
  }, []);
  const { enqueueSnackbar } = useSnackbar();
  const [isBankDrawerOpen, setIsBankDrawerOpen] = useState(false);
  const [isBankDetailsOpen, setIsBankDetailsOpen] = useState(false);
  const [openOthers, setOpenOthers] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [provider, setProvider] = useState('');
  const [selectedBank, setSelectedBank] = useState(null);
  const [amount, setAmount] = useState('');
  const [gameProvider, setGameProvider] = useState([]);
  const [loading, setLoading] = useState(false);
  const [memberBank, setMemberBank] = useState(memberInfo?.userBank || []);
  const minAmount = memberInfo?.minWithdraw;
  const walletBalance = memberInfo?.walletBalance;
  const amountNumber = Number(amount);
  const isAmountTooLow = amount && amountNumber < minAmount;
  const isAmountOverBalance = amount && amountNumber > walletBalance;
  const currency = memberInfo?.currency;
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();

  // Only invalid if there's actually a number and it's out of bounds
  const isInvalid = amount && (isAmountTooLow || isAmountOverBalance);
  let infoMessage = '';
  if (!amount) {
    infoMessage = '';
  } else if (isAmountTooLow) {
    infoMessage = `Minimum withdrawal is ${currency} ${minAmount}`;
  } else if (isAmountOverBalance) {
    infoMessage = `Available amount: ${currency} ${walletBalance}`;
  }

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');

      const payload = {
        'add_withdraw[withdraw_from]': provider,
        'add_withdraw[withdraw_amount]': amount,
        'add_withdraw[withdraw_bank]': selectedBank,
      };

      const { data: response } = await conObjForm.post('v1/user/add-withdraw', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response?.success) {
        enqueueSnackbar('Withdrawal request submitted successfully!', { variant: 'success' });
        handleRestore();
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        enqueueSnackbar(response?.message || 'Submission failed', { variant: 'error' });
      }
    } catch (error) {
      console.error('STATUS:', error.response?.status);
      console.error('DATA:', error.response?.data);

      const apiError = error.response?.data?.error;
      const errorMessage = Array.isArray(apiError)
        ? apiError[0]
        : error.response?.data?.message || 'An error occurred during submission';

      const cleanMessage = errorMessage.replace(/\[.*?\]\s*/, '');

      enqueueSnackbar(cleanMessage, {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchWithdrawData = async () => {
    setLoading(true);

    const token = localStorage.getItem('accessToken');

    conObj
      .get('v1/user/pre-withdraw', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        if (response?.data?.success) {
          const apiData = response.data.data;
          const apiBanks = apiData.user_bank || [];

          setGameProvider(apiData.game_provider || []);
          setMemberBank(apiBanks);

          if (!provider) setProvider('');

          const defaultBank = apiBanks.find((b) => b.is_default === 1) || apiBanks[0];
          if (defaultBank) {
            setSelectedBank(defaultBank.id);
          }
        }
      })
      .catch((error) => {
        console.error('Fetch error:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (memberBank.length > 0) {
      const defaultBank = memberBank.find((b) => b.is_default === 1);
      if (defaultBank) {
        setSelectedBank(defaultBank.id);
      }
    }
  }, [memberBank]);

  useEffect(() => {
    fetchWithdrawData();
  }, []);

  useEffect(() => {
    if (isDesktop && memberBank.length === 0 && !loading) {
      setIsBankDetailsOpen(true);
    }
  }, [memberBank.length, isDesktop, loading]);

  const handleRestore = () => {
    setProvider(''); // Resets Provider dropdown
    setAmount(''); // Resets Withdrawal Amount text field
    const defaultBank = memberBank.find((b) => b.is_default === 1) || memberBank[0];
    setSelectedBank(defaultBank ? defaultBank.id : null);
    setOpenOthers(false); // Collapses any open notice sections
    fetchWithdrawData();
  };

  const EmptyBankState = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%',
        minHeight: '80vh',
        px: 1,
        pt: 2,
        pb: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          bgcolor: '#EDEDF1',
          borderRadius: '8px',
          p: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Iconify icon="mdi:information" sx={{ color: '#FF4842', width: 20, height: 20, flexShrink: 0 }} />
        <Typography
          sx={{
            fontSize: '14px',
            color: '#4B5563',
            textAlign: 'left',
            fontWeight: 400,
          }}
        >
          You can add up to <span style={{ color: '#FF4842', fontWeight: 700 }}>5</span> bank accounts
        </Typography>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Image
            src={Login}
            sx={{
              width: 240,
              height: 240,
              filter: 'drop-shadow(0px 10px 20px rgba(0,0,0,0.05))',
            }}
          />
        </Box>

        <Typography
          sx={{
            color: '#8991A7',
            fontWeight: 400,
            fontSize: '15px',
          }}
        >
          {translate('no_withdraw_bank_account_yet')}
        </Typography>
      </Box>
    </Box>
  );
  const selectedBankObj = memberBank.find((b) => b.id === selectedBank);
  const selectedBankIcon = banksIcons[getBankKey(selectedBankObj?.bank_name)] || Card;

  const isSubmitDisabled = !amount || !selectedBank; // || isInvalid || loading;
  return (
    <>
      {loading && <LoadingScreen loadable={true} />}
      {!isDesktop && (
        <NonRootHeader label={'withdrawal'} addBank={true} onAddBankClick={() => setIsBankDetailsOpen(true)} />
      )}
      <Box sx={{ p: { md: 3, xs: 1 }, backgroundColor: isDesktop ? '#F6F7F9' : 'unset' }}>
        {memberBank.length === 0 && !loading && !isDesktop ? (
          <EmptyBankState />
        ) : (
          <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
            {/* Header / Balance */}
            {/* Group 1: Wallet Balance Header */}
            <Box
            // sx={{
            //   px: isMobile ? theme.spacing(3) : 0,
            // }}
            >
              <MemberBalanceSection handleRestore={handleRestore} />
            </Box>

            <FormSection sx={{ background: isMobile ? '#F6F7F9 !important' : ' #fff' }}>
              {/* Provider Selection */}
              <Box
                sx={{
                  background: isMobile ? '#fff' : '#ffffff',
                  padding: isMobile ? theme.spacing(2) : 0,
                  borderRadius: '8px',
                  marginBottom: isMobile ? theme.spacing(1) : 0,
                }}
              >
                <Label>{translate('provider')}</Label>
                <Select
                  fullWidth
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  displayEmpty
                  sx={{
                    mb: 3,
                    borderRadius: '8px',
                    bgcolor: '#F9FAFB',
                    ...(provider !== '' && {
                      border: `2px solid ${theme.palette.main}`,
                      background: `${theme.palette.gradients.buttonPrimary} !important`,
                    }),
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      fontWeight: 600,
                    },
                    '& .MuiSelect-icon': {
                      color: '#000',
                    },
                  }}
                >
                  <MenuItem value="">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <img src={GameConsole} alt="" width={20} height={20} />
                      {translate('select_provider')}
                    </Box>
                  </MenuItem>

                  {gameProvider.map((item) => {
                    const isSelected = provider === item.id;
                    return (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          fontWeight: isSelected ? 700 : 500,
                          '&.Mui-selected': {
                            background: theme.palette.gradients.buttonPrimary,
                            color: '#000',
                            '&:hover': {
                              background: theme.palette.gradients.buttonPrimary2,
                            },
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <img src={GameConsole} alt="" width={20} height={20} />
                          {item.provider_name}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>

                {/* Withdrawal Amount */}
                <Label>{translate('withdrawal_amount')}</Label>
                <TextField
                  fullWidth
                  placeholder={`${translate('min_amount')}: ${minAmount}`}
                  value={amount}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d*\.?\d*$/.test(val)) {
                      setAmount(val);
                    }
                  }}
                  error={isInvalid}
                  sx={{
                    ...inputStyles,
                    '& .MuiOutlinedInput-root': {
                      ...inputStyles['& .MuiOutlinedInput-root'],
                      ...(isInvalid && {
                        border: '1px solid #FF4842',
                        backgroundColor: '#FFF5F5',
                      }),
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#9CA3AF',
                      opacity: 1,
                      fontWeight: 10,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ fontWeight: 700, color: isInvalid ? '#FF4842' : '#111' }}>
                          {currency}
                        </Typography>
                      </InputAdornment>
                    ),
                    endAdornment: amount && (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setAmount('')} size="small">
                          <Iconify
                            icon={isInvalid ? 'mdi:close-circle' : 'mdi:close-circle'}
                            color={isInvalid ? '#FF4842' : '#D1D5DB'}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Helper Text / Available Amount Label */}
                {infoMessage && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Iconify
                      icon="mdi:information-outline"
                      sx={{ color: isInvalid ? '#FF4842' : '#6B7280', width: 16 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: isInvalid ? '#FF4842' : '#6B7280',
                        fontWeight: isInvalid ? 600 : 400,
                      }}
                    >
                      {infoMessage}
                    </Typography>
                  </Box>
                )}

                <Grid container spacing={1} mt={1} mb={2}>
                  {suggestedAmounts.map((val) => {
                    const buttonValue = val === 'All' ? walletBalance : val;
                    const isSelected = amount === buttonValue;

                    return (
                      <Grid item xs={4} md={2} key={val}>
                        <Button
                          fullWidth
                          onClick={() => setAmount(buttonValue)}
                          sx={{
                            py: 1,
                            fontWeight: 700,
                            borderRadius: '8px',
                            border: `2px solid ${palette.header.register_btn.border}`,
                            background: isSelected ? theme.palette.gradients.buttonPrimary : '#F3F4F6',
                            color: '#000',
                          }}
                        >
                          {val === 'All' ? translate('all') : val}
                        </Button>
                      </Grid>
                    );
                  })}
                </Grid>

                <Stack spacing={0.5} mb={3}>
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Iconify icon="mdi:information" sx={{ mr: 0.5, width: 16, color: '#EF4444' }} />
                    {translate('available_amount')} {currency} {walletBalance}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Iconify icon="mdi:information" sx={{ mr: 0.5, width: 16, color: '#EF4444' }} />{' '}
                    {translate('turnover_requirement')} 0.00
                  </Typography>
                </Stack>
              </Box>

              {/* Bank Selection */}
              <Box
                sx={{
                  background: isMobile ? '#fff' : '#ffffff',
                  p: isMobile ? theme.spacing(2) : 0,
                  borderRadius: '8px',
                  mt: isMobile ? theme.spacing(2) : 0,
                  mb: isMobile ? theme.spacing(1) : 0,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Label sx={{ mb: 0 }}>{translate('preferred_bank')}</Label>
                  {!isMobile && (
                    <Button
                      onClick={() => setIsBankDetailsOpen(true)} // Toggle state to true
                      endIcon={<Iconify icon="mdi:plus-circle-outline" />}
                      sx={{ color: 'primary.main', fontWeight: 700, textTransform: 'none' }}
                    >
                      {translate('add_bank')}
                    </Button>
                  )}
                </Stack>
                {/* Bank Selection Section */}
                {isMobile ? (
                  /* Mobile Selector Button */
                  <Button
                    fullWidth
                    onClick={() => setIsBankDrawerOpen(true)}
                    sx={{
                      justifyContent: 'space-between',
                      p: 2,
                      borderRadius: '12px',
                      bgcolor: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      textTransform: 'none',
                      color: '#000',
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      {selectedBank ? (
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              bgcolor: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                            }}
                          >
                            <img src={selectedBankIcon} alt={selectedBankObj?.bank_name} width={20} height={20} />
                          </Box>
                          <Typography fontWeight={600}>{selectedBankObj?.bank_name}</Typography>
                        </Stack>
                      ) : (
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Iconify icon="mdi:bank" sx={{ color: '#6B7280' }} />
                          <Typography fontWeight={600}>{translate('select_your_bank')}</Typography>
                        </Stack>
                      )}
                    </Stack>
                    <Iconify icon="mdi:chevron-right" />
                  </Button>
                ) : (
                  <Grid container spacing={2} mb={4}>
                    {memberBank
                      .slice()
                      .sort((a, b) => a.is_default - b.is_default)
                      .map((bank) => {
                        const isSelected = selectedBank === bank.id;
                        const maskedAccount =
                          bank.account_number.length > 6
                            ? `${bank.account_number.slice(0, 4)}***${bank.account_number.slice(-2)}`
                            : bank.account_number;
                        const icon = banksIcons[getBankKey(bank.bank_name)] || Card;
                        return (
                          <Grid item xs={12} sm={6} md={4} key={bank.id}>
                            <BankCard isSelected={isSelected} onClick={() => setSelectedBank(bank.id)}>
                              <Box
                                sx={{
                                  bgcolor: '#fff',
                                  p: 0.5,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  boxShadow: isSelected ? 'none' : '0px 2px 4px rgba(0,0,0,0.1)',
                                }}
                              >
                                <img src={icon} alt={bank.bank_name} width={24} height={24} />
                              </Box>

                              <Stack spacing={0}>
                                <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '10px' }}>
                                  {bank.bank_name}
                                </Typography>
                                <Typography fontWeight={700}>{maskedAccount}</Typography>
                              </Stack>
                            </BankCard>
                          </Grid>
                        );
                      })}
                  </Grid>
                )}{' '}
              </Box>

              {/* Important Notice */}
              {!isMobile && (
                <Box sx={{ mt: 4 }}>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <Iconify icon="mdi:alert" sx={{ color: '#EF4444' }} />
                    <Typography sx={{ color: '#EF4444', fontWeight: 700 }}>{translate('important_notice')}</Typography>
                  </Stack>
                  <Box sx={{ bgcolor: '#F9FAFB', p: 2, borderRadius: '8px', border: '1px solid #F3F4F6' }}>
                    <ul
                      style={{ margin: 0, paddingLeft: '20px', color: '#6B7280', fontSize: '13px', lineHeight: '1.8' }}
                    >
                      <li>{translate('kindly_check_live_chat')}</li>
                      <li>{translate('withdrawal_bank_account_name')}</li>
                      <li>{translate('some_game_provider')}</li>
                      <li>{translate('please_make_sure_turn_over')}</li>
                    </ul>
                  </Box>
                </Box>
              )}

              {/* Footer Info */}
              <Typography sx={{ mt: 3, color: '#EF4444', fontWeight: 700, fontSize: '14px' }}>
                * {translate('withdrawal_remaining_limit')} 3/3
              </Typography>

              <Button
                fullWidth
                variant="contained"
                disabled={isSubmitDisabled || loading}
                onClick={handleSubmit}
                sx={{
                  display: 'block',
                  mt: 2,
                  height: 45,
                  borderRadius: '30px',
                  fontSize: '16px',
                  fontWeight: 700,
                  mx: 'auto',
                  textTransform: 'none',
                  boxShadow: 'none',
                  border: `2px solid ${isSubmitDisabled || loading ? '#E5E7EB' : palette.header.register_btn.border}`,
                  background: isSubmitDisabled || loading ? '#E5E7EB' : theme.palette.gradients.buttonPrimary,
                  color: isSubmitDisabled || loading ? '#9CA3AF' : 'rgb(0, 0, 0)808',
                  '&:hover': {
                    background: isSubmitDisabled || loading ? '#E5E7EB' : theme.palette.gradients.buttonPrimary, // same as normal background
                  },
                }}
              >
                {loading ? 'Submitting...' : `${translate('submit')}`}
              </Button>
            </FormSection>

            <CustomDrawer
              open={isBankDrawerOpen}
              onClose={() => setIsBankDrawerOpen(false)}
              title={translate('preferred_bank')}
            >
              <Box sx={{ p: 2, pb: 5 }}>
                <Stack spacing={2}>
                  {memberBank.map((bank) => {
                    const isSelected = selectedBank === bank.id;
                    const maskedAccount =
                      bank.account_number.length > 6
                        ? `${bank.account_number.slice(0, 4)}***${bank.account_number.slice(-2)}`
                        : bank.account_number;
                    const icon = banksIcons[getBankKey(bank.bank_name)] || Card;
                    return (
                      <Box
                        key={bank.id}
                        onClick={() => {
                          setSelectedBank(bank.id);
                          setIsBankDrawerOpen(false);
                        }}
                        sx={{
                          p: 2,
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          border: `2px solid ${isSelected ? palette.header.register_btn.border : '#F3F4F6'}`,
                          background: isSelected ? theme.palette.gradients.buttonPrimary : '#F9FAFB',
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box sx={{ bgcolor: '#fff', p: 0.5, borderRadius: '50%', display: 'flex' }}>
                            <img src={icon} alt="bank" width={24} height={24} />
                          </Box>
                          <Typography fontWeight={700}>
                            {bank.bank_name} {maskedAccount}
                          </Typography>
                        </Stack>

                        {/* Checkbox Icon for Selected Bank */}
                        {isSelected && (
                          <Iconify icon="mdi:checkbox-marked" sx={{ color: 'primary.main', fontSize: '24px' }} />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </CustomDrawer>
          </Box>
        )}
        <AddBankAccountDialog
          handleOpen={isBankDetailsOpen}
          handleClose={() => setIsBankDetailsOpen(false)}
          onSuccess={() => {
            fetchWithdrawData();
          }}
        />
      </Box>
    </>
  );
};

export default Withdraw;
